import React, {
  useState,
  useEffect,
  useContext,
} from "react";

import {
  auth,
  db,
} from "../firebase/firebaseConfig";

import {
  collection,
  getDocs,
  where,
  query,
  limit,
} from "firebase/firestore";

import {
  useNavigate,
  useParams,
} from "react-router-dom";

import Alertas from "./Alertas";
import ActualizarModeloDb from "../firebase/ActualizarModeloDb";
import useObtenerModelo from "../hooks/useObtenerModelo";

import { UserContext } from "../context/UserContext";

import {
  Container,
  Segment,
  Form,
  Button,
  Header,
  Icon,
  Message,
} from "semantic-ui-react";

const ActualizaModelo = () => {
  const user = auth.currentUser;
  const { users } = useContext(UserContext);

  const navigate = useNavigate();
  const { id } = useParams();

  const [modelo] = useObtenerModelo(id);

  // ==========================================================
  // ESTADOS
  // ==========================================================

  const [
    nuevoCampo,
    setNuevoCampo,
  ] = useState("");

  /*
   * Nombre ORIGINAL persistido.
   *
   * La validación de propagación siempre debe hacerse
   * contra este valor.
   */
  const [
    modeloOriginal,
    setModeloOriginal,
  ] = useState("");

  const [
    alerta,
    cambiarAlerta,
  ] = useState({});

  const [
    estadoAlerta,
    cambiarEstadoAlerta,
  ] = useState(false);

  const [
    isSaving,
    setIsSaving,
  ] = useState(false);

  const [
    verificandoUso,
    setVerificandoUso,
  ] = useState(false);

  const [
    modeloBloqueado,
    setModeloBloqueado,
  ] = useState(false);

  // ==========================================================
  // VOLVER
  // ==========================================================

  const volver = () => {
    navigate(
      "/misequipos/agregarmodelo"
    );
  };

  // ==========================================================
  // VERIFICAR SI EL MODELO ESTÁ SIENDO UTILIZADO
  // ==========================================================

  const verificarModeloEnUso =
    async (nombreModelo) => {
      if (!users?.emp_id) {
        throw new Error(
          "No se encontró la empresa del usuario"
        );
      }

      const modeloNormalizado =
        String(
          nombreModelo || ""
        )
          .toLocaleUpperCase()
          .trim();

      if (!modeloNormalizado) {
        return false;
      }

      // ======================================================
      // 1. EQUIPOS
      // ======================================================

      const qEquipos =
        query(
          collection(
            db,
            "equipos"
          ),

          where(
            "emp_id",
            "==",
            users.emp_id
          ),

          where(
            "modelo",
            "==",
            modeloNormalizado
          ),

          limit(1)
        );

      const snapEquipos =
        await getDocs(
          qEquipos
        );

      if (!snapEquipos.empty) {
        return true;
      }

      // ======================================================
      // 2. INGRESOS SERVICIO TÉCNICO
      // ======================================================

      const qIngresos =
        query(
          collection(
            db,
            "ingresostcab"
          ),

          where(
            "emp_id",
            "==",
            users.emp_id
          ),

          where(
            "modelo",
            "==",
            modeloNormalizado
          ),

          limit(1)
        );

      const snapIngresos =
        await getDocs(
          qIngresos
        );

      if (!snapIngresos.empty) {
        return true;
      }

      return false;
    };

  // ==========================================================
  // VERIFICAR DUPLICADO
  // ==========================================================

  const verificarNombreDuplicado =
    async (nombreModelo) => {
      if (!users?.emp_id) {
        return false;
      }

      const modeloNormalizado =
        String(
          nombreModelo || ""
        )
          .toLocaleUpperCase()
          .trim();

      if (!modeloNormalizado) {
        return false;
      }

      const qModelos =
        query(
          collection(
            db,
            "modelos"
          ),

          where(
            "emp_id",
            "==",
            users.emp_id
          ),

          where(
            "modelo",
            "==",
            modeloNormalizado
          )
        );

      const snapModelos =
        await getDocs(
          qModelos
        );

      /*
       * El documento actual no es duplicado.
       */
      return snapModelos.docs.some(
        (doc) =>
          doc.id !== id
      );
    };

  // ==========================================================
  // ACTUALIZAR
  // ==========================================================

  const handleSubmit =
    async (e) => {
      e.preventDefault();

      cambiarEstadoAlerta(false);
      cambiarAlerta({});

      const nombreNuevo =
        String(
          nuevoCampo || ""
        )
          .toLocaleUpperCase()
          .trim();

      const originalNormalizado =
        String(
          modeloOriginal || ""
        )
          .toLocaleUpperCase()
          .trim();

      // ======================================================
      // VALIDAR VACÍO
      // ======================================================

      if (!nombreNuevo) {
        cambiarEstadoAlerta(true);

        cambiarAlerta({
          tipo: "error",
          mensaje:
            "El Modelo no puede estar vacío.",
        });

        return;
      }

      // ======================================================
      // VALIDAR QUE HAYA CAMBIO
      // ======================================================

      if (
        nombreNuevo ===
        originalNormalizado
      ) {
        cambiarEstadoAlerta(true);

        cambiarAlerta({
          tipo: "error",
          mensaje:
            "No se han realizado cambios en el Modelo.",
        });

        return;
      }

      setIsSaving(true);

      try {
        // ====================================================
        // REVALIDAR PROPAGACIÓN
        //
        // IMPORTANTE:
        // buscamos siempre modeloOriginal.
        // ====================================================

        const enUso =
          await verificarModeloEnUso(
            modeloOriginal
          );

        if (enUso) {
          setModeloBloqueado(true);

          cambiarEstadoAlerta(true);

          cambiarAlerta({
            tipo: "error",
            mensaje:
              "Este modelo ya está siendo utilizado en el sistema y no puede modificarse.",
          });

          return;
        }

        // ====================================================
        // VALIDAR DUPLICADO
        // ====================================================

        const duplicado =
          await verificarNombreDuplicado(
            nombreNuevo
          );

        if (duplicado) {
          cambiarEstadoAlerta(true);

          cambiarAlerta({
            tipo: "error",
            mensaje:
              "Ya existe este Modelo.",
          });

          return;
        }

        // ====================================================
        // ACTUALIZAR
        // ====================================================

        await ActualizarModeloDb({
          id:
            id,

          modelo:
            nombreNuevo,

          userMod:
            user?.email || "",

          fechaMod:
            new Date(),
        });

        setModeloOriginal(
          nombreNuevo
        );

        setNuevoCampo(
          nombreNuevo
        );

        cambiarEstadoAlerta(true);

        cambiarAlerta({
          tipo: "exito",
          mensaje:
            "Modelo actualizado correctamente.",
        });
      } catch (error) {
        console.error(
          "Error actualizando Modelo:",
          error
        );

        cambiarEstadoAlerta(true);

        cambiarAlerta({
          tipo: "error",
          mensaje:
            "No fue posible actualizar el Modelo.",
        });
      } finally {
        setIsSaving(false);
      }
    };

  // ==========================================================
  // CARGAR MODELO Y COMPROBAR USO
  // ==========================================================

  useEffect(() => {
    if (
      !modelo?.modelo ||
      !users?.emp_id
    ) {
      return;
    }

    const nombreModelo =
      String(
        modelo.modelo || ""
      )
        .toLocaleUpperCase()
        .trim();

    setNuevoCampo(
      nombreModelo
    );

    setModeloOriginal(
      nombreModelo
    );

    const comprobarUso =
      async () => {
        setVerificandoUso(true);

        try {
          const enUso =
            await verificarModeloEnUso(
              nombreModelo
            );

          setModeloBloqueado(
            enUso
          );
        } catch (error) {
          console.error(
            "Error verificando uso del Modelo:",
            error
          );

          /*
           * Si no podemos verificar,
           * bloqueamos por seguridad.
           */
          setModeloBloqueado(true);

          cambiarEstadoAlerta(true);

          cambiarAlerta({
            tipo: "error",
            mensaje:
              "No fue posible verificar si el Modelo puede modificarse.",
          });
        } finally {
          setVerificandoUso(false);
        }
      };

    comprobarUso();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    modelo,
    users?.emp_id,
  ]);

  // ==========================================================
  // RENDER
  // ==========================================================

  return (
    <Container
      fluid
      style={{
        maxWidth: 1400,
        padding: "24px 28px",
        background: "#f8fafc",
        minHeight: "100vh",
      }}
    >
      {/* ==================================================== */}
      {/* ENCABEZADO */}
      {/* ==================================================== */}

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 14,
          marginBottom: 20,
        }}
      >
        <div
          style={{
            width: 48,
            height: 48,
            minWidth: 48,
            borderRadius: 12,
            background: "#e5f7f7",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Icon
            name="edit outline"
            style={{
              margin: 0,
              color: "#0d9da5",
              fontSize: 21,
            }}
          />
        </div>

        <div>
          <Header
            as="h2"
            style={{
              margin: 0,
              color: "#172b4d",
              fontSize: 24,
              fontWeight: 700,
            }}
          >
            Actualizar Modelo
          </Header>

          <div
            style={{
              color: "#8793a5",
              fontSize: 13,
              marginTop: 4,
            }}
          >
            Modifica el modelo de equipamiento médico
          </div>
        </div>
      </div>

      {/* ==================================================== */}
      {/* TARJETA */}
      {/* ==================================================== */}

      <Segment
        style={{
          borderRadius: 8,
          border:
            "1px solid #e5e9ef",
          boxShadow:
            "0 2px 6px rgba(0,0,0,0.04)",
          padding: 0,
          background: "#ffffff",
          overflow: "hidden",
        }}
      >
        {/* CABECERA */}

        <div
          style={{
            padding:
              "14px 18px",
            borderBottom:
              "1px solid #edf0f4",
            display: "flex",
            alignItems: "center",
            gap: 9,
            color: "#253858",
            fontSize: 14,
            fontWeight: 600,
          }}
        >
          <Icon
            name="tag"
            style={{
              color: "#0d9da5",
            }}
          />

          Datos del Modelo
        </div>

        <div
          style={{
            padding: 22,
          }}
        >
          {/* ================================================ */}
          {/* VERIFICANDO */}
          {/* ================================================ */}

          {verificandoUso && (
            <Message
              icon
              size="small"
              style={{
                marginTop: 0,
              }}
            >
              <Icon
                name="circle notched"
                loading
              />

              <Message.Content>
                <Message.Header>
                  Verificando Modelo
                </Message.Header>

                Estamos comprobando si el Modelo puede ser modificado.
              </Message.Content>
            </Message>
          )}

          {/* ================================================ */}
          {/* BLOQUEADO */}
          {/* ================================================ */}

          {!verificandoUso &&
            modeloBloqueado && (
              <Message
                warning
                icon
                style={{
                  marginTop: 0,
                }}
              >
                <Icon name="lock" />

                <Message.Content>
                  <Message.Header>
                    Modelo protegido
                  </Message.Header>

                  Este modelo ya está siendo utilizado en el sistema y no puede modificarse.
                </Message.Content>
              </Message>
            )}

          {/* ================================================ */}
          {/* DISPONIBLE */}
          {/* ================================================ */}

          {!verificandoUso &&
            !modeloBloqueado &&
            modeloOriginal && (
              <Message
                positive
                icon
                size="small"
                style={{
                  marginTop: 0,
                }}
              >
                <Icon name="unlock" />

                <Message.Content>
                  <Message.Header>
                    Modelo disponible para edición
                  </Message.Header>

                  Este modelo todavía no ha sido utilizado en el sistema.
                </Message.Content>
              </Message>
            )}

          {/* ================================================ */}
          {/* FORMULARIO */}
          {/* ================================================ */}

          <Form
            size="large"
            onSubmit={
              handleSubmit
            }
          >
            <Form.Field>
              <label
                style={{
                  color: "#344563",
                  fontSize: 13,
                  fontWeight: 600,
                  marginBottom: 7,
                  display: "block",
                }}
              >
                Modelo
              </label>

              <Form.Input
                fluid
                type="text"
                name="modelo"
                icon="tag"
                iconPosition="left"
                placeholder="Ingrese Modelo Equipamiento Médico"
                value={
                  nuevoCampo
                }
                disabled={
                  isSaving ||
                  verificandoUso ||
                  modeloBloqueado
                }
                onChange={(e) =>
                  setNuevoCampo(
                    e.target.value
                  )
                }
              />
            </Form.Field>

            {/* ============================================== */}
            {/* BOTONES */}
            {/* ============================================== */}

            <div
              style={{
                display: "flex",
                justifyContent:
                  "flex-end",
                alignItems: "center",
                gap: 10,
                marginTop: 30,
                paddingTop: 18,
                borderTop:
                  "1px solid #edf0f4",
                flexWrap: "wrap",
              }}
            >
              <Button
                type="button"
                onClick={
                  volver
                }
                disabled={
                  isSaving
                }
                style={{
                  minWidth: 110,
                  background: "#ffffff",
                  color: "#526273",
                  border:
                    "1px solid #d7dee7",
                  borderRadius: 6,
                  boxShadow: "none",
                }}
              >
                <Icon
                  name="arrow left"
                />

                Volver
              </Button>

              {!modeloBloqueado && (
                <Button
                  type="submit"
                  loading={
                    isSaving
                  }
                  disabled={
                    isSaving ||
                    verificandoUso ||
                    !modeloOriginal
                  }
                  style={{
                    minWidth: 125,
                    background:
                      "#0d9da5",
                    color: "#ffffff",
                    border:
                      "1px solid #0d9da5",
                    borderRadius: 6,
                    boxShadow: "none",
                  }}
                >
                  {!isSaving && (
                    <Icon
                      name="save"
                    />
                  )}

                  Actualizar
                </Button>
              )}
            </div>
          </Form>
        </div>
      </Segment>

      {/* ==================================================== */}
      {/* ALERTAS */}
      {/* ==================================================== */}

      <Alertas
        tipo={
          alerta.tipo
        }
        mensaje={
          alerta.mensaje
        }
        estadoAlerta={
          estadoAlerta
        }
        cambiarEstadoAlerta={
          cambiarEstadoAlerta
        }
      />
    </Container>
  );
};

export default ActualizaModelo;